import { createAsyncThunk, createSlice, SerializedError } from "@reduxjs/toolkit";
import jwtDecode from "jwt-decode";
import { AuthorizeRequest, AuthorizeResponse } from "../models/Authorize";
import { Authorize, RevokeToken } from "../services/authorization";
import {
  getAccessTokenFromLocalStorage,
  removeAuthTokenValuesFromLocalStorage,
  setAuthTokenValuesInLocalStorage,
} from "../utils/localStorageAuthHelper";
import { SliceCallState } from "./sliceCallState";

export interface TokenData {
  userId?: number;
  exp?: number;
  roles?: string[];
  userType?: number;
}

export interface AuthState {
  token?: AuthorizeResponse;
  error?: SerializedError;
  decodedToken: TokenData;
  loading: SliceCallState;
}

export interface AuthAction {
  payload: AuthorizeResponse & { error?: SerializedError };
  type: string;
}

const tokenProperties = {
  roles: "http://schemas.microsoft.com/ws/2008/06/identity/claims/role",
};

const initialState: AuthState = {
  token: undefined,
  decodedToken: {
    exp: undefined,
  },
  loading: SliceCallState.pending,
};

export const authorize = createAsyncThunk<AuthorizeResponse, AuthorizeRequest, { rejectValue: SerializedError }>(
  "api/authorize",
  async (authData: AuthorizeRequest, thunkAPI) => {
    try {
      return await Authorize(authData);
    } catch (error) {
      const err = error as SerializedError;
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const logout = createAsyncThunk("api/logout", async (_, thunkAPI) => {
  try {
    return await RevokeToken();
  } catch (error) {
    const err = error as SerializedError;
    return thunkAPI.rejectWithValue(err);
  }
});

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    getDataFromLocalStorage: (state: AuthState) => {
      const token = getAccessTokenFromLocalStorage();
      state.token = token;
      if (token && token.access_token) {
        const decodedToken = jwtDecode<TokenData>(token.access_token);
        if (decodedToken && !decodedToken.roles) {
          decodedToken.roles = decodedToken[tokenProperties.roles as keyof typeof decodedToken] as string[];
        }
        state.decodedToken = decodedToken;
      }
      state.loading = SliceCallState.idle;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(authorize.pending, (state: AuthState) => {
      state.loading = SliceCallState.pending;
    });

    builder.addCase(authorize.fulfilled, (state: AuthState, action: AuthAction) => {
      state.token = action.payload;
      state.loading = SliceCallState.succeeded;
      state.decodedToken = jwtDecode(action.payload.access_token);

      setAuthTokenValuesInLocalStorage(action.payload);
    });

    builder.addCase(authorize.rejected, (state, action) => {
      state.loading = SliceCallState.failed;
      state.error = action.payload;
      throw new Error(action.payload?.message);
    });

    builder.addCase(logout.pending, (state) => {
      state.loading = SliceCallState.pending;
    });

    builder.addCase(logout.fulfilled, (state: AuthState) => {
      state.token = undefined;
      state.decodedToken = initialState.decodedToken;
      state.loading = SliceCallState.succeeded;
      removeAuthTokenValuesFromLocalStorage();
    });

    builder.addCase(logout.rejected, (state: AuthState, action) => {
      state.loading = SliceCallState.failed;
      state.token = undefined;
      state.decodedToken = initialState.decodedToken;
      removeAuthTokenValuesFromLocalStorage();
      throw new Error(action.error?.message);
    });
  },
});

export const { getDataFromLocalStorage } = authSlice.actions;
